import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  DatePicker,
  InputNumber,
  Radio,
  Row,
  Col,
  Image,
  Typography,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { categoriaLabel } from '../../../utils/categoriaLabel';

const { Text } = Typography;

const MODALIDADES = [
  { value: 'LIGA', label: 'Liga' },
  { value: 'AMERICANO', label: 'Americano' },
  { value: 'TORNEO', label: 'Torneo' },
];

const SEXOS = [
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MIXTO', label: 'Mixto' },
];

const CANTIDAD_PAREJAS_MIN = 2;
const CANTIDAD_PAREJAS_MAX = 256;
const CANTIDADES_SETS = [1, 3];

const modalidadLabel = (value) => MODALIDADES.find((m) => m.value === value)?.label || value;
const sexoLabel = (value) => SEXOS.find((s) => s.value === value)?.label || value;

const FASE_LABELS = {
  INSCRIPCION: 'Inscripción',
  ARMADO_GRUPOS: 'Armado grupos',
  FASE_GRUPOS: 'Fase grupos',
  LLAVE: 'Llave',
  FINALIZADO: 'Finalizado',
};

const getCategoriasPermitidas = (categoriaId, categoriasExtraIds, categorias, inscripcionLibre = false) => {
  if (!categorias.length) return [];
  if (inscripcionLibre) return categorias;
  if (!categoriaId) return [];
  const categoriaTorneoId = Number(categoriaId);
  const extras = Array.isArray(categoriasExtraIds)
    ? categoriasExtraIds.map(Number).filter((id) => Number.isInteger(id) && id > 0 && id !== categoriaTorneoId)
    : [];
  const permitidasIds = new Set([categoriaTorneoId, ...extras]);
  return categorias.filter((c) => permitidasIds.has(Number(c.cat_id)));
};

const extrasDesdeFlagsLegacy = (record, categorias) => {
  if (!record || !categorias.length || !record.tor_categoria_id) return [];
  const ids = categorias.map((c) => Number(c.cat_id));
  const idx = ids.indexOf(Number(record.tor_categoria_id));
  if (idx === -1) return [];
  const extras = [];
  if (record.tor_permite_categoria_inferior && idx > 0) extras.push(ids[idx - 1]);
  if (record.tor_permite_categoria_superior && idx < ids.length - 1) extras.push(ids[idx + 1]);
  return extras;
};

const getExtrasPermitidasRecord = (record, categorias) => {
  const fromArray = Array.isArray(record.tor_categorias_permitidas)
    ? record.tor_categorias_permitidas
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0 && id !== Number(record.tor_categoria_id))
    : [];
  if (fromArray.length > 0) return fromArray;
  return extrasDesdeFlagsLegacy(record, categorias);
};

const formatCategoriasInscripcion = (record, categorias) => {
  if (record.tor_inscripcion_libre) return 'Inscripción libre (todas)';
  const permitidas = getCategoriasPermitidas(
    record.tor_categoria_id,
    getExtrasPermitidasRecord(record, categorias),
    categorias,
    false
  );
  if (!permitidas.length) return record.categoria_descripcion || '-';
  if (permitidas.length === 1) return `Solo ${categoriaLabel(permitidas[0])}`;
  return permitidas.map((c) => categoriaLabel(c)).join(', ');
};

const formatPeriodoInscripcion = (record) => {
  const desde = record.tor_inscripcion_desde;
  const hasta = record.tor_inscripcion_hasta;
  if (!desde && !hasta) return '-';
  if (desde && hasta) {
    return `${dayjs(desde).format('DD/MM/YYYY')} - ${dayjs(hasta).format('DD/MM/YYYY')}`;
  }
  return dayjs(desde || hasta).format('DD/MM/YYYY');
};

const TorneosList = () => {
  const navigate = useNavigate();
  const [torneos, setTorneos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTorneo, setEditingTorneo] = useState(null);
  const [form] = Form.useForm();
  const categoriaIdWatch = Form.useWatch('categoriaId', form);
  const categoriasPermitidasWatch = Form.useWatch('categoriasPermitidas', form);
  const inscripcionLibreWatch = Form.useWatch('inscripcionLibre', form);
  const conSeniaWatch = Form.useWatch('conSenia', form);
  const cantidadSetsWatch = Form.useWatch('cantidadSets', form);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoBase64, setFotoBase64] = useState(null);
  const [fotoTipo, setFotoTipo] = useState(null);
  const [fotoRemoved, setFotoRemoved] = useState(false);
  const fileInputRef = useRef(null);

  const loadCategorias = async () => {
    try {
      const response = await apiClient.get('/categorias');
      if (response.ok) {
        const data = await response.json();
        setCategorias(Array.isArray(data) ? data.filter((c) => c.cat_activo) : []);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const loadTorneos = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/torneos/torneos');
      if (response.ok) {
        const data = await response.json();
        setTorneos(data);
      } else {
        message.error('Error al cargar los torneos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los torneos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategorias();
    loadTorneos();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetFoto = () => {
    setFotoPreview(null);
    setFotoBase64(null);
    setFotoTipo(null);
    setFotoRemoved(false);
  };

  const handleCreate = () => {
    setEditingTorneo(null);
    form.resetFields();
    resetFoto();
    form.setFieldsValue({
      activo: true,
      modalidad: 'TORNEO',
      sexo: 'MIXTO',
      cantidadParejas: 32,
      cantidadSets: 3,
      superTieBreak: true,
      precioPareja: 0,
      conSenia: false,
      senia: 0,
      categoriasPermitidas: [],
      inscripcionLibre: false,
      companeroDirecto: false,
      elegirHorarioInscripcion: false,
      tamanoGrupo: 4,
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingTorneo(record);
    resetFoto();
    form.setFieldsValue({
      fecha: record.tor_fecha ? dayjs(record.tor_fecha) : null,
      inscripcionDesde: record.tor_inscripcion_desde ? dayjs(record.tor_inscripcion_desde) : null,
      inscripcionHasta: record.tor_inscripcion_hasta ? dayjs(record.tor_inscripcion_hasta) : null,
      nombre: record.tor_nombre,
      modalidad: record.tor_modalidad,
      sexo: record.tor_sexo,
      cantidadParejas: record.tor_cantidad_parejas,
      cantidadSets: record.tor_cantidad_sets ?? 3,
      superTieBreak: record.tor_super_tie_break !== false,
      categoriaId: record.tor_categoria_id,
      categoriasPermitidas: getExtrasPermitidasRecord(record, categorias),
      inscripcionLibre: record.tor_inscripcion_libre ?? false,
      companeroDirecto: record.tor_companero_directo ?? false,
      elegirHorarioInscripcion: record.tor_elegir_horario_inscripcion ?? false,
      precioPareja: record.tor_precio_pareja,
      conSenia: record.tor_con_senia ?? (Number(record.tor_senia) > 0),
      senia: record.tor_senia,
      activo: record.tor_activo,
      tamanoGrupo: record.tor_tamano_grupo ?? 4,
    });
    if (record.tor_foto_base64) {
      const tipo = record.tor_foto_tipo || 'image/jpeg';
      setFotoBase64(record.tor_foto_base64);
      setFotoTipo(tipo);
      setFotoPreview(`data:${tipo};base64,${record.tor_foto_base64}`);
    }
    setModalVisible(true);
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.warning('Elegí una imagen (JPG, PNG, etc.)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.startsWith('data:')) {
        const base64 = result.split(',')[1];
        setFotoBase64(base64);
        setFotoTipo(file.type);
        setFotoPreview(result);
        setFotoRemoved(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const quitarFoto = () => {
    setFotoBase64(null);
    setFotoTipo(null);
    setFotoPreview(null);
    setFotoRemoved(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const body = {
        fecha: values.fecha.format('YYYY-MM-DD'),
        inscripcionDesde: values.inscripcionDesde.format('YYYY-MM-DD'),
        inscripcionHasta: values.inscripcionHasta.format('YYYY-MM-DD'),
        nombre: values.nombre,
        modalidad: values.modalidad,
        sexo: values.sexo,
        cantidadParejas: values.cantidadParejas,
        cantidadSets: values.cantidadSets,
        superTieBreak: values.cantidadSets === 3 ? values.superTieBreak !== false : false,
        categoriaId: values.categoriaId,
        categoriasPermitidas: values.inscripcionLibre
          ? []
          : (Array.isArray(values.categoriasPermitidas) ? values.categoriasPermitidas : [])
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0 && id !== Number(values.categoriaId)),
        inscripcionLibre: values.inscripcionLibre === true,
        companeroDirecto: values.companeroDirecto === true,
        elegirHorarioInscripcion: values.elegirHorarioInscripcion === true,
        precioPareja: values.precioPareja ?? 0,
        conSenia: values.conSenia === true,
        senia: values.conSenia === true ? (values.senia ?? 0) : 0,
        activo: values.activo,
        tamanoGrupo: values.tamanoGrupo,
      };

      if (fotoRemoved) {
        body.clearFoto = true;
      } else if (fotoBase64) {
        body.fotoBase64 = fotoBase64;
        body.fotoTipo = fotoTipo;
      }

      if (editingTorneo) {
        const response = await apiClient.put(`/torneos/torneos/${editingTorneo.tor_id}`, body);
        if (response.ok) {
          message.success('Torneo actualizado correctamente');
          setModalVisible(false);
          loadTorneos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el torneo');
        }
      } else {
        const response = await apiClient.post('/torneos/torneos', body);
        if (response.ok) {
          message.success('Torneo creado correctamente');
          setModalVisible(false);
          loadTorneos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el torneo');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el torneo');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/torneos/torneos/${id}`);
      if (response.ok) {
        message.success('Torneo eliminado correctamente');
        loadTorneos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el torneo');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el torneo');
    }
  };

  const formatMoney = (value) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value ?? 0);

  const columns = [
    {
      title: 'Foto',
      dataIndex: 'tor_foto_base64',
      key: 'foto',
      width: 72,
      render: (_, record) =>
        record.tor_foto_base64 ? (
          <Image
            src={`data:${record.tor_foto_tipo || 'image/jpeg'};base64,${record.tor_foto_base64}`}
            alt={record.tor_nombre}
            width={48}
            height={48}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            preview
          />
        ) : (
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: 'rgba(255,255,255,0.06)',
            }}
          />
        ),
    },
    {
      title: 'Fecha torneo',
      dataIndex: 'tor_fecha',
      key: 'tor_fecha',
      width: 110,
      render: (fecha) => (fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Inscripción',
      key: 'periodo_inscripcion',
      width: 180,
      render: (_, record) => formatPeriodoInscripcion(record),
    },
    {
      title: 'Nombre',
      dataIndex: 'tor_nombre',
      key: 'tor_nombre',
    },
    {
      title: 'Modalidad',
      dataIndex: 'tor_modalidad',
      key: 'tor_modalidad',
      width: 110,
      render: (value) => <Tag>{modalidadLabel(value)}</Tag>,
    },
    {
      title: 'Sexo',
      dataIndex: 'tor_sexo',
      key: 'tor_sexo',
      width: 110,
      render: (value) => sexoLabel(value),
    },
    {
      title: 'Parejas',
      dataIndex: 'tor_cantidad_parejas',
      key: 'tor_cantidad_parejas',
      width: 90,
      align: 'center',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_descripcion',
      key: 'categoria',
      width: 120,
      render: (text, record) =>
        text ? (
          <Tag color={record.categoria_color || 'default'}>{text}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'Categorías',
      key: 'categorias_inscripcion',
      width: 160,
      render: (_, record) => formatCategoriasInscripcion(record, categorias),
    },
    {
      title: 'Precio',
      dataIndex: 'tor_precio_pareja',
      key: 'tor_precio_pareja',
      width: 110,
      render: (value) => formatMoney(value),
    },
    {
      title: 'Seña',
      key: 'tor_senia',
      width: 110,
      render: (_, record) =>
        record.tor_con_senia ? formatMoney(record.tor_senia) : <Text type="secondary">Sin seña</Text>,
    },
    {
      title: 'Fase',
      dataIndex: 'tor_fase',
      key: 'tor_fase',
      width: 120,
      render: (fase) => (
        <Tag color={fase === 'FINALIZADO' ? 'gold' : fase === 'INSCRIPCION' ? 'default' : 'blue'}>
          {FASE_LABELS[fase] || fase || 'Inscripción'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'tor_activo',
      key: 'tor_activo',
      width: 100,
      render: (activo) => (
        <Tag color={activo ? 'green' : 'red'}>{activo ? 'Activo' : 'Inactivo'}</Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            icon={<TeamOutlined />}
            onClick={() => navigate(`/torneos/torneos/${record.tor_id}/gestion`)}
            size="small"
          >
            Gestionar
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">
            Editar
          </Button>
          <Popconfirm
            title="¿Está seguro de eliminar este torneo?"
            onConfirm={() => handleDelete(record.tor_id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title="Gestión de Torneos"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadTorneos} loading={loading}>
              Actualizar
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              Nuevo Torneo
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={torneos}
          loading={loading}
          rowKey="tor_id"
          scroll={{ x: isMobile ? 1400 : 'auto' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} torneos`,
          }}
        />

        <Modal
          title={editingTorneo ? 'Editar Torneo' : 'Nuevo Torneo'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => setModalVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '95%' : 720}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="fecha"
                  label="Fecha del torneo"
                  rules={[{ required: true, message: 'La fecha del torneo es requerida' }]}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nombre"
                  label="Nombre del torneo"
                  rules={[
                    { required: true, message: 'El nombre es requerido' },
                    { max: 150, message: 'Máximo 150 caracteres' },
                  ]}
                >
                  <Input placeholder="Ej: Torneo de verano" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="inscripcionDesde"
                  label="Inscripción desde"
                  rules={[{ required: true, message: 'La fecha de inicio es requerida' }]}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="inscripcionHasta"
                  label="Inscripción hasta"
                  dependencies={['inscripcionDesde', 'fecha']}
                  rules={[
                    { required: true, message: 'La fecha de fin es requerida' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const desde = getFieldValue('inscripcionDesde');
                        if (value && desde && value.isBefore(desde, 'day')) {
                          return Promise.reject(
                            new Error('No puede ser anterior al inicio de inscripción')
                          );
                        }
                        const fechaTorneo = getFieldValue('fecha');
                        if (value && fechaTorneo && value.isAfter(fechaTorneo, 'day')) {
                          return Promise.reject(
                            new Error('No puede ser posterior a la fecha del torneo')
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Foto del torneo">
              <Space direction="vertical" style={{ width: '100%' }}>
                {fotoPreview && (
                  <Image
                    src={fotoPreview}
                    alt="Vista previa"
                    style={{ maxWidth: 200, maxHeight: 160, borderRadius: 8, objectFit: 'cover' }}
                  />
                )}
                <Space wrap>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
                    Subir foto
                  </Button>
                  {fotoPreview && (
                    <Button danger onClick={quitarFoto}>
                      Quitar foto
                    </Button>
                  )}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  JPG, PNG u otro formato de imagen.
                </Text>
              </Space>
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="modalidad"
                  label="Modalidad"
                  rules={[{ required: true, message: 'Seleccioná una modalidad' }]}
                >
                  <Select placeholder="Seleccionar modalidad">
                    {MODALIDADES.map((m) => (
                      <Select.Option key={m.value} value={m.value}>
                        {m.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="sexo"
                  label="Sexo"
                  rules={[{ required: true, message: 'Seleccioná el sexo' }]}
                >
                  <Radio.Group>
                    {SEXOS.map((s) => (
                      <Radio.Button key={s.value} value={s.value}>
                        {s.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="cantidadParejas"
                  label="Cantidad de parejas"
                  rules={[
                    { required: true, message: 'Ingresá la cantidad de parejas' },
                    {
                      type: 'number',
                      min: CANTIDAD_PAREJAS_MIN,
                      max: CANTIDAD_PAREJAS_MAX,
                      message: `Debe ser entre ${CANTIDAD_PAREJAS_MIN} y ${CANTIDAD_PAREJAS_MAX}`,
                    },
                  ]}
                  extra="Podés poner cualquier cantidad (ej. 15, 24, 32)."
                >
                  <InputNumber
                    min={CANTIDAD_PAREJAS_MIN}
                    max={CANTIDAD_PAREJAS_MAX}
                    precision={0}
                    style={{ width: '100%' }}
                    placeholder="Ej: 24"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="cantidadSets"
                  label="Sets por partido"
                  rules={[{ required: true, message: 'Seleccioná la cantidad de sets' }]}
                >
                  <Radio.Group>
                    {CANTIDADES_SETS.map((n) => (
                      <Radio.Button key={n} value={n}>
                        {n}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            {cantidadSetsWatch === 3 && (
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    name="superTieBreak"
                    label="Set decisivo (3.er set)"
                    valuePropName="checked"
                    extra="Si está activo, el tercer set se juega como super tie-break a 10 puntos. Si no, es un set completo (6-0 a 7-6)."
                  >
                    <Switch checkedChildren="Super tie-break" unCheckedChildren="Set completo" />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="tamanoGrupo"
                  label="Tamaño de grupos"
                  rules={[{ required: true, message: 'Seleccioná el tamaño de los grupos' }]}
                  extra="Al armar zonas se priorizan grupos de este tamaño; el resto se completa con grupos del otro tamaño."
                >
                  <Radio.Group>
                    <Radio.Button value={3}>Grupos de 3</Radio.Button>
                    <Radio.Button value={4}>Grupos de 4</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="categoriaId"
                  label="Categoría"
                  rules={[{ required: true, message: 'Seleccioná una categoría' }]}
                >
                  <Select
                    placeholder="Seleccionar categoría"
                    showSearch
                    optionFilterProp="children"
                    onChange={(nuevaCategoriaId) => {
                      const actuales = form.getFieldValue('categoriasPermitidas') || [];
                      form.setFieldsValue({
                        categoriasPermitidas: actuales.filter(
                          (id) => Number(id) !== Number(nuevaCategoriaId)
                        ),
                      });
                    }}
                  >
                    {categorias.map((cat) => (
                      <Select.Option key={cat.cat_id} value={cat.cat_id}>
                        {categoriaLabel(cat)}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="precioPareja"
                  label="Precio por pareja"
                  rules={[{ required: true, message: 'Ingresá el precio' }]}
                >
                  <InputNumber prefix="$" style={{ width: '100%' }} min={0} step={0.01} precision={2} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="conSenia" label="Seña" valuePropName="checked">
                  <Switch checkedChildren="Con seña" unCheckedChildren="Sin seña" />
                </Form.Item>
              </Col>
              {conSeniaWatch && (
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="senia"
                    label="Monto de la seña"
                    rules={[
                      { required: true, message: 'Ingresá el monto de la seña' },
                      {
                        type: 'number',
                        min: 0.01,
                        message: 'La seña debe ser mayor a 0',
                      },
                    ]}
                  >
                    <InputNumber prefix="$" style={{ width: '100%' }} min={0.01} step={0.01} precision={2} />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <Form.Item label="Categorías aceptadas para inscribirse">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary">La categoría del torneo siempre está permitida.</Text>
                <Form.Item name="inscripcionLibre" valuePropName="checked" noStyle>
                  <Checkbox
                    onChange={(e) => {
                      if (e.target.checked) {
                        form.setFieldsValue({ categoriasPermitidas: [] });
                      }
                    }}
                  >
                    Inscripción libre (cualquier categoría, damas y caballeros)
                  </Checkbox>
                </Form.Item>
                <Form.Item name="categoriasPermitidas" noStyle>
                  <Select
                    mode="multiple"
                    allowClear
                    disabled={inscripcionLibreWatch || !categoriaIdWatch}
                    placeholder={
                      categoriaIdWatch
                        ? 'Agregar otras categorías permitidas'
                        : 'Primero elegí la categoría del torneo'
                    }
                    optionFilterProp="children"
                    style={{ width: '100%' }}
                  >
                    {categorias
                      .filter((cat) => Number(cat.cat_id) !== Number(categoriaIdWatch))
                      .map((cat) => (
                        <Select.Option key={cat.cat_id} value={cat.cat_id}>
                          {categoriaLabel(cat)}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
                {inscripcionLibreWatch ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Podrán inscribirse jugadores de cualquier categoría (damas y caballeros).
                  </Text>
                ) : categoriaIdWatch ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Podrán inscribirse jugadores de:{' '}
                    {getCategoriasPermitidas(
                      categoriaIdWatch,
                      categoriasPermitidasWatch,
                      categorias,
                      false
                    )
                      .map((c) => categoriaLabel(c))
                      .join(', ') || '—'}
                  </Text>
                ) : null}
              </Space>
            </Form.Item>

            <Form.Item
              name="companeroDirecto"
              label="Modo de inscripción de pareja"
              valuePropName="checked"
              extra="Apagado: el compañero debe aceptar una invitación. Encendido: el jugador elige compañero e inscribe la pareja desde Mis Torneos."
            >
              <Switch
                checkedChildren="Elección directa"
                unCheckedChildren="Con invitación"
              />
            </Form.Item>

            <Form.Item
              name="elegirHorarioInscripcion"
              label="Día y franja horaria"
              valuePropName="checked"
              extra="Encendido: el jugador elige día y franja al inscribirse desde Mis Torneos. Apagado: lo asigna el club en el armado de grupos."
            >
              <Switch
                checkedChildren="Elige el jugador"
                unCheckedChildren="Lo asigna el club"
              />
            </Form.Item>

            <Form.Item name="activo" label="Activo" valuePropName="checked">
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </MainLayout>
  );
};

export default TorneosList;
